import axios from "axios";
import {
  createAsyncThunk,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";
import helpers from "helpers";
import {
  createEntitySlice,
  serializeAxiosError,
} from "../config/reducer.utils";
import { TypedPrompt } from "../interface/prompt.model";

/**
 *   Reducer used for front-end, with prompt.model.ts
 *   Interface.ts can be use in both front-end and back-end! But prefer using prompt.model.ts
 */

const initialState = {
  loading: false,
  errorMessage: [] as any,
  entities: [],
  entity: null,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  updateSuccessNew: false,
};

const apiUrl = "post";

// Actions

export const getEntities = createAsyncThunk(
  "prompt/fetch_entity_list",
  async (object: any) => {
    const EndURL = helpers.buildEndUrl(object);
    const requestUrl = `${apiUrl}/list-prompt${EndURL}`;
    return axios.get<any>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  "prompt/fetch_entity",
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/detail-prompt/${id}`;
    return axios.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  "prompt/create_entity",
  async (entity: TypedPrompt, thunkAPI) => {
    const result = await axios.post<TypedPrompt>(
      `${apiUrl}/create-prompt`,
      helpers.cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createEntityNew = createAsyncThunk(
  "prompt/create_entity_new",
  async (entity: TypedPrompt, thunkAPI) => {
    const result = await axios.post<TypedPrompt>(
      `${apiUrl}/create-prompt`,
      helpers.cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const importEntity = createAsyncThunk(
  "prompt/import_entity",
  async (entity: TypedPrompt, thunkAPI) => {
    const result = await axios.post<TypedPrompt>(
      `${apiUrl}/import-prompt`,
      helpers.cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  "prompt/update_entity",
  async (entity: TypedPrompt, thunkAPI) => {
    const result = await axios.patch<TypedPrompt>(
      `${apiUrl}/update-prompt`,
      helpers.cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntityNew = createAsyncThunk(
  "prompt/update_entity_new",
  async (entity: TypedPrompt, thunkAPI) => {
    const result = await axios.patch<TypedPrompt>(
      `${apiUrl}/update-prompt`,
      helpers.cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  "prompt/partial_update_entity",
  async (entity: TypedPrompt, thunkAPI) => {
    const result = await axios.patch<TypedPrompt>(
      `${apiUrl}/${entity._id}`,
      helpers.cleanEntity(entity)
    );
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  "prompt/delete_entity",
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/delete-prompt/${id}`;
    thunkAPI.dispatch(getEntities({}));
    return axios.delete<any>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const Reducer_Prompt = createEntitySlice({
  name: "prompt",
  initialState,
  reducers: {
    clearError: (state) => {
      state.errorMessage = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEntity.rejected, (state, action) => {
        state.loading = false;
        state.entity = null;
      })
      .addCase(createEntity.rejected, (state, action) => {
        state.loading = false;
        state.entity = null;
        state.errorMessage = action.payload;
      })
      // .addCase(createEntityNew.rejected, (state, action) => {
      //   state.loading = false;
      //   state.entity = null;
      //   state.errorMessage = action.payload;
      // })
      .addCase(importEntity.rejected, (state, action) => {
        state.loading = false;
        state.entity = null;
        state.errorMessage = action.payload;
      })

      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers["x-total-count"], 10),
        };
      })
      .addMatcher(
        isRejected(
          createEntity,
          importEntity,
          updateEntity,
          partialUpdateEntity,
          deleteEntity
        ),
        (state, action) => {
          state.loading = false;
          state.updating = false;
          state.updateSuccess = false;
          state.errorMessage = action.payload;
        }
      )
      .addMatcher(
        isRejected(createEntityNew, updateEntityNew),
        (state, action) => {
          state.loading = false;
          state.updating = false;
          state.updateSuccessNew = false;
          state.errorMessage = action.payload;
        }
      )
      .addMatcher(
        isFulfilled(
          createEntity,
          importEntity,
          updateEntity,
          partialUpdateEntity
        ),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccess = true;
          state.entity = action.payload.data;
        }
      )
      .addMatcher(
        isFulfilled(createEntityNew, updateEntityNew),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccessNew = true;
          state.entity = action.payload.data;
        }
      )
      .addMatcher(isFulfilled(deleteEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = null;
      })
      .addMatcher(isPending(deleteEntity), (state) => {
        state.updating = true;
        state.loading = true;
        state.updateSuccess = false;
        state.entity = null;
      })
      .addMatcher(isPending(getEntities, getEntity), (state) => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updateSuccessNew = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(
          createEntity,
          importEntity,
          updateEntity,
          partialUpdateEntity
        ),
        (state) => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      )
      .addMatcher(isPending(createEntityNew, updateEntityNew), (state) => {
        state.errorMessage = null;
        state.updateSuccessNew = false;
        state.updating = true;
      });
  },
});

export const { clearError, reset } = Reducer_Prompt.actions;

// Reducer
export default Reducer_Prompt.reducer;
